import { useContext, useMemo } from "react";
import { useIntl } from "react-intl";

import { JobStatus } from "@nens/rana/api-v1";
import { TenantContext } from "@nens/ranalib";

import { ProgressDialog, ProgressDialogItem } from "@/components/progress-dialog";
import { jobIsRunning } from "@/utils/job-is-running";
import { navigateOutsideRouter } from "@/utils/navigate-outside-router";

import { TrackedProcessRun } from "../types/process-provider-types";
import { getJobUrl } from "../utils/get-job-url";

function toDialogStatus(stateType: JobStatus | undefined): ProgressDialogItem["status"] {
  if (jobIsRunning(stateType)) return "inProgress";
  if (stateType === "completed") return "success";
  return "error";
}

interface ProcessProgressDialogProps {
  processes: TrackedProcessRun[];
}

export function ProcessProgressDialog({ processes }: ProcessProgressDialogProps) {
  const intl = useIntl();
  const { tenant } = useContext(TenantContext);

  const items = useMemo(
    (): ProgressDialogItem[] =>
      processes.map((p) => {
        const status = toDialogStatus(p.job.state.type);
        const progress = (p.job.state.progress ?? 0) * 100 || undefined;

        let sublabel: string | undefined;
        if (status === "success") {
          sublabel = p.processCompleteMessage;
        } else if (status === "error") {
          sublabel = intl.formatMessage({
            id: "process.progress_dialog.failed",
            defaultMessage: "Process failed",
          });
        }
        return {
          id: p.job.id,
          label: p.job.name,
          progress,
          sublabel,
          status,
          onClick: () => {
            navigateOutsideRouter(getJobUrl({ tenant, process: p }));
          },
        };
      }),
    [intl, processes, tenant]
  );

  if (!processes.length) return null;

  const runningCount = processes.filter((p) => jobIsRunning(p.job.state.type)).length;

  const title =
    runningCount > 0
      ? intl.formatMessage(
          {
            id: "process.progress_dialog.running",
            defaultMessage: "Running {count, plural, one {# process} other {# processes}}",
          },
          { count: runningCount }
        )
      : intl.formatMessage({
          id: "process.progress_dialog.done",
          defaultMessage: "All processes done",
        });

  return <ProgressDialog title={title} items={items} />;
}
